import { z } from 'zod';
import {
  HealthRecommendationCategorySchema,
  HealthRecommendationFocusSchema,
} from './health-recommendations';
import {
  KruxDeliveryEvidenceSchema,
  KruxEvidenceReachabilitySchema,
} from './krux-delivery';

// ---------------------------------------------------------------------------
// The Krux (issue #2956, KRX-A of the Krux epic #2955).
//
// A recommendation is a read-model row: it vanishes on the next scan, nobody
// owns it, and nothing remembers whether anyone acted. A KRUX is that same
// recommendation reified into an owned, persisted decision.
//
// Identity is the engine's stable recommendation id (#2893/#2918) qualified by
// the repository it was measured in, because the recommendation id is only
// unique within one repository's layered graph. Nothing here mints an id of its
// own, so the same finding accepted twice is the same Krux.
//
// KRX-A carried three states. KRX-B (#2957) adds the two the scan can prove on
// its own: `delivered` when the underlying recommendation stops being reported,
// `regressed` when it comes back. Expectations are still KRX-C.
// ---------------------------------------------------------------------------

export const KRUX_STATES = [
  'proposed',
  'accepted',
  'dismissed',
  'delivered',
  'regressed',
] as const;
export const KruxStateSchema = z.enum(KRUX_STATES);
export type KruxState = z.infer<typeof KruxStateSchema>;

/**
 * The states a persisted transition may record. `proposed` is deliberately
 * absent: it is what an engine-born recommendation already is, so persisting it
 * would store a row that says nothing a caller did not already know.
 */
export const KruxTransitionSchema = z.enum([
  'accepted',
  'dismissed',
  'delivered',
  'regressed',
]);
export type KruxTransition = z.infer<typeof KruxTransitionSchema>;

/**
 * The two transitions a scan may write on its own. A person accepts or
 * dismisses; only the engine delivers or regresses, and only with a scan to
 * point at — which is why `scanId` is required for exactly these.
 */
export const KRUX_VERIFIED_STATES = ['delivered', 'regressed'] as const;
export const KruxVerifiedStateSchema = z.enum(KRUX_VERIFIED_STATES);
export type KruxVerifiedState = z.infer<typeof KruxVerifiedStateSchema>;

/** Owner-minted Kruxes are KRX-C; today every Krux comes from the engine. */
export const KruxSourceSchema = z.enum(['engine']);
export type KruxSource = z.infer<typeof KruxSourceSchema>;

/**
 * Dismissal reasons are learning data, not a formality — "the evidence is
 * wrong" and "correct but not now" are different signals about the engine, and
 * a freeform note alone could not be counted.
 */
export const KRUX_DISMISSAL_REASONS = [
  'already-done',
  'duplicate',
  'not-a-problem',
  'not-now',
  'out-of-scope',
  'wrong-evidence',
] as const;
export const KruxDismissalReasonSchema = z.enum(KRUX_DISMISSAL_REASONS);
export type KruxDismissalReason = z.infer<typeof KruxDismissalReasonSchema>;

export const MAX_KRUX_NOTE_LENGTH = 500;
export const MAX_KRUX_REFERENCE_LENGTH = 300;

/**
 * The measurement that justified a verified transition, copied onto the row at
 * the moment it was recorded (#3144).
 *
 * `scanId` names a `repo_scans` row, and only two scans per repository are
 * retained — so two scans later the reference resolves to nothing and the
 * outcome would be an assertion rather than a record. These are the values the
 * scan diff actually judged, small enough to travel on every transition: the
 * commit that was measured, when it was measured, and how many recommendations
 * that scan reported, which is the set this Krux's presence or absence was read
 * against. Nothing derivable from the row itself is repeated here.
 */
export const KruxScanEvidenceSchema = z.object({
  headSha: z.string().min(1),
  scannedAt: z.iso.datetime(),
  recommendationCount: z.number().int().nonnegative(),
});
export type KruxScanEvidence = z.infer<typeof KruxScanEvidenceSchema>;

/**
 * WHICH identity a Krux is bound to (#2957).
 *
 * Absent on every row written before this existed, which is why the schema
 * defaults it to `recommendation`: those rows are all engine recommendations,
 * and defaulting the other way would reinterpret the entire existing history as
 * findings it was never about.
 *
 * `finding` means `recommendationId` carries a GENUINE `find-v1:` capture
 * finding identity, stored verbatim. Nothing is derived in either direction — a
 * recommendation is never mapped onto findings and a finding is never mapped
 * onto a recommendation, because no such mapping exists and inventing one would
 * fabricate the evidence this issue is about. The column is shared because
 * `kruxId` is derived from it and the read path requires it; the discriminator
 * is what says how to read it.
 */
export const KRUX_BINDING_KINDS = ['recommendation', 'finding'] as const;
export const KruxBindingKindSchema = z.enum(KRUX_BINDING_KINDS);
export type KruxBindingKind = z.infer<typeof KruxBindingKindSchema>;

/**
 * The immutable capture a finding was accepted FROM.
 *
 * This is the baseline half of every later comparison, which is why it is
 * recorded at acceptance rather than looked up afterwards: the newest capture of
 * a repository changes, and a baseline resolved at verification time would make
 * the same accepted work compare against a different starting point on every
 * scan. Pinned here, the acceptance names exactly one capture forever.
 *
 * `runId` is nullable because a capture outlives the run row that produced it,
 * and an invented run id is a worse record than an absent one.
 */
export const KruxFindingBindingSchema = z.object({
  captureId: z.string().min(1),
  headSha: z.string().min(1),
  runId: z.string().min(1).nullable(),
  areaId: z.string().min(1),
  areaRevision: z.number().int().positive(),
});
export type KruxFindingBinding = z.infer<typeof KruxFindingBindingSchema>;

/**
 * How reachable the evidence behind one transition is, resolved when the
 * transition was read rather than stored with it.
 *
 * The first two are the cases `scanId`'s own absence documents: `unrecorded`
 * for a row written before outcomes existed, `none` for a human decision that
 * carries no scan. `unresolvable` is the third (#3144) — a scan was named and
 * the `repo_scans` row it names is gone, so the claim is "verified by a scan
 * you can no longer inspect", which is neither of the other two and must not be
 * mistaken for a bug. `resolved` is the only value under which the reference
 * itself may still be cited.
 */
export const KRUX_EVIDENCE_STATES = [
  'unrecorded',
  'none',
  'resolved',
  'unresolvable',
] as const;
export const KruxEvidenceStateSchema = z.enum(KRUX_EVIDENCE_STATES);
export type KruxEvidenceState = z.infer<typeof KruxEvidenceStateSchema>;

export const KruxDismissalSchema = z.object({
  reason: KruxDismissalReasonSchema,
  /** Freeform detail. Empty is allowed; the enum is the countable part. */
  note: z.string().max(MAX_KRUX_NOTE_LENGTH),
});
export type KruxDismissal = z.infer<typeof KruxDismissalSchema>;

/**
 * One append-only transition. History rows are evidence: a correction appends a
 * new row rather than editing an old one, which is why the Convex rule for this
 * table denies `modify` outright.
 */
export const KruxEventSchema = z.object({
  kruxId: z.string().min(1),
  recommendationId: z.string().min(1),
  repo: z.string().min(1),
  source: KruxSourceSchema,
  state: KruxTransitionSchema,
  dismissal: KruxDismissalSchema.nullable(),
  /** A pasted issue/PR URL. Kruxit never creates one — see epic #2955. */
  reference: z.string().max(MAX_KRUX_REFERENCE_LENGTH).nullable(),
  /**
   * The scan that evidenced this transition. Null for a human decision, which
   * is about intent rather than about any one scan; required for `delivered`
   * and `regressed`, so an outcome can always be traced to the measurement
   * that produced it rather than asserted.
   */
  scanId: z.string().nullable().default(null),
  /**
   * Whether `scanId` could still be followed when this row was read. Defaulted
   * like `scanId` so a payload serialised before #3144 still parses, and
   * defaulted to `unrecorded` deliberately: it is the only value that licenses
   * no claim, and a missing resolution must never be read as a surviving scan.
   */
  evidence: KruxEvidenceStateSchema.default('unrecorded'),
  /** Null for a decision that measured nothing, and on rows predating #3144. */
  scanEvidence: KruxScanEvidenceSchema.nullable().default(null),
  /**
   * Which identity this row is bound to. Defaulted so every row written before
   * #2957 parses as the engine recommendation it was.
   */
  bindingKind: KruxBindingKindSchema.default('recommendation'),
  /** The pinned capture a finding was accepted from; null for a recommendation. */
  findingBinding: KruxFindingBindingSchema.nullable().default(null),
  /**
   * The comparison that evidenced a finding-bound outcome, in full.
   *
   * Separate from `scanEvidence`, which is the recommendation path's measurement
   * and says nothing about captures. A reader must be able to tell a
   * recommendation-set outcome from a capture-comparison one, because they do
   * not license the same claims.
   */
  deliveryEvidence: KruxDeliveryEvidenceSchema.nullable().default(null),
  /**
   * Whether the captures `deliveryEvidence` names can be followed NOW.
   *
   * Resolved on every read and never stored, exactly like `evidence` above and
   * for the same reason: capture retention moves underneath a stored row, so a
   * value frozen at write time would keep claiming a comparison is inspectable
   * long after the evidence behind it had gone. Defaulted to `unrecorded`
   * because that is the only value licensing no claim.
   */
  deliveryReachability: KruxEvidenceReachabilitySchema.default('unrecorded'),
  recordedAt: z.iso.datetime(),
  recordedBy: z.string().min(1),
});
export type KruxEvent = z.infer<typeof KruxEventSchema>;

/** The folded current state of one Krux, newest transition first in `history`. */
export const KruxSchema = z.object({
  kruxId: z.string().min(1),
  recommendationId: z.string().min(1),
  repo: z.string().min(1),
  source: KruxSourceSchema,
  state: KruxStateSchema,
  dismissal: KruxDismissalSchema.nullable(),
  reference: z.string().nullable(),
  /**
   * The scan behind the newest transition, when a scan produced it. Defaulted
   * rather than required so a payload serialised before outcomes existed still
   * parses as the human decision it was, instead of being dropped.
   */
  scanId: z.string().nullable().default(null),
  /** Both resolved for the newest transition, on the same terms as `scanId`. */
  evidence: KruxEvidenceStateSchema.default('unrecorded'),
  scanEvidence: KruxScanEvidenceSchema.nullable().default(null),
  /** Carried forward from the acceptance: what this Krux is bound to. */
  bindingKind: KruxBindingKindSchema.default('recommendation'),
  findingBinding: KruxFindingBindingSchema.nullable().default(null),
  /** The comparison behind the newest transition, when one produced it. */
  deliveryEvidence: KruxDeliveryEvidenceSchema.nullable().default(null),
  /** Resolved for the newest transition, on the same terms as `evidence`. */
  deliveryReachability: KruxEvidenceReachabilitySchema.default('unrecorded'),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  history: z.array(KruxEventSchema),
});
export type Krux = z.infer<typeof KruxSchema>;

/** Paste-ready text. Copying it is the only integration — no GitHub writes. */
export const KruxPromptsSchema = z.object({
  issueTitle: z.string().min(1),
  issueBody: z.string().min(1),
  lanePrompt: z.string().min(1),
});
export type KruxPrompts = z.infer<typeof KruxPromptsSchema>;

const KruxWriteTargetSchema = z.object({
  org: z.string().min(1),
  repo: z.string().min(1),
  recommendationId: z.string().min(1),
  /**
   * The focus the recommendation was listed under. The transition is only
   * recorded if the id still appears in that live collection, and the engine
   * returns a different top ten per focus — so a caller that listed under one
   * focus must say so, or its own row would look fabricated.
   */
  focus: HealthRecommendationFocusSchema.optional(),
});

export const KruxAcceptRequestSchema = KruxWriteTargetSchema.extend({
  action: z.literal('accept'),
  reference: z.string().max(MAX_KRUX_REFERENCE_LENGTH).optional(),
});

/** Dismissal without a reason is refused at the contract, not at the UI. */
export const KruxDismissRequestSchema = KruxWriteTargetSchema.extend({
  action: z.literal('dismiss'),
  reason: KruxDismissalReasonSchema,
  note: z.string().max(MAX_KRUX_NOTE_LENGTH).optional(),
});

export const KruxWriteRequestSchema = z.discriminatedUnion('action', [
  KruxAcceptRequestSchema,
  KruxDismissRequestSchema,
]);
export type KruxWriteRequest = z.infer<typeof KruxWriteRequestSchema>;

export const KruxRouteErrorSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'payment_required',
    'invalid_request',
    'org_not_found',
    'recommendation_not_found',
    'unavailable',
    'upstream',
  ]),
});
export type KruxRouteError = z.infer<typeof KruxRouteErrorSchema>;

export const KruxListResponseSchema = z.object({
  kruxes: z.array(KruxSchema),
});
export type KruxListResponse = z.infer<typeof KruxListResponseSchema>;

export const KruxWriteResponseSchema = z.object({
  krux: KruxSchema,
  prompts: KruxPromptsSchema.nullable(),
});
export type KruxWriteResponse = z.infer<typeof KruxWriteResponseSchema>;

/**
 * The Krux key. Lower-cased repository slug so it matches the `repoKey`
 * convention the scan tables already use, joined to the engine's stable
 * recommendation id — which already embeds a hash of the candidate's identity,
 * so no further disambiguation is needed inside one repository.
 */
export function kruxIdFor(
  input: Readonly<{ repo: string; recommendationId: string }>,
): string {
  return `${input.repo.trim().toLowerCase()}#${input.recommendationId.trim()}`;
}

/**
 * The identity scheme a recommendation id was minted under.
 *
 * Ids look like `health-rec-v1-<category>-<hash>` (#2893/#2918). The version
 * segment is what makes two scans comparable: if the engine later mints `v2`
 * ids, a `v1` Krux is not absent from the new scan — it is unaddressable by it,
 * and reading absence as delivery would silently mark every open Krux done.
 * Returns `null` for anything that does not carry a recognisable version.
 */
export function recommendationIdentityVersion(
  recommendationId: string,
): string | null {
  return (
    /^health-rec-(?<version>v\d+)-/u.exec(recommendationId)?.groups?.[
      'version'
    ] ?? null
  );
}

/**
 * The category a recommendation id was minted for.
 *
 * Ids embed it as `health-rec-<version>-<category>-<hash>`. Verification needs
 * it to tell whether the scan it is diffing against even measured the thing:
 * absence is only evidence when the scan could have found it. Matched against
 * the known vocabulary rather than split on hyphens, because several category
 * names contain one.
 */
export function recommendationIdentityCategory(
  recommendationId: string,
): z.infer<typeof HealthRecommendationCategorySchema> | null {
  const version = recommendationIdentityVersion(recommendationId);
  if (version === null) return null;
  const rest = recommendationId.slice(`health-rec-${version}-`.length);
  return (
    HealthRecommendationCategorySchema.options.find((category) =>
      rest.startsWith(`${category}-`),
    ) ?? null
  );
}

/**
 * Fold an append-only history into current Kruxes, newest first.
 *
 * Ordering is by `recordedAt` and then by arrival, so two transitions written
 * inside the same millisecond still resolve deterministically rather than
 * depending on which the database happened to return first.
 */
export function foldKruxEvents(events: readonly KruxEvent[]): readonly Krux[] {
  const ordered = events
    .map((event, index) => ({ event, index }))
    .toSorted(
      (left, right) =>
        Date.parse(left.event.recordedAt) -
          Date.parse(right.event.recordedAt) || left.index - right.index,
    );
  const byId = new Map<string, Krux>();
  for (const { event } of ordered) {
    const existing = byId.get(event.kruxId);
    byId.set(event.kruxId, {
      kruxId: event.kruxId,
      recommendationId: event.recommendationId,
      repo: event.repo,
      source: event.source,
      state: event.state,
      dismissal: event.dismissal,
      // A later transition that carries no reference keeps the one already
      // pasted; clearing it would need its own explicit transition.
      reference: event.reference ?? existing?.reference ?? null,
      // Unlike `reference`, these are NOT carried forward: they are the scan
      // that evidenced *this* transition and the measurement it was judged on,
      // so a human correction after a delivery must not appear to have been
      // evidenced by that delivery's scan.
      scanId: event.scanId,
      evidence: event.evidence,
      scanEvidence: event.scanEvidence,
      // The comparison that evidenced THIS transition, on the same terms as
      // `scanEvidence` above: not carried forward, so a later human correction
      // does not inherit the comparison that justified an earlier delivery.
      deliveryEvidence: event.deliveryEvidence,
      deliveryReachability: event.deliveryReachability,
      // Carried forward, unlike the two above, because these are the Krux's
      // IDENTITY rather than one transition's evidence. The capture a finding was
      // accepted from is fixed at acceptance; a later `delivered` row records the
      // comparison, not a new binding, and must not appear to have unbound the
      // work it just closed.
      // NOT carried forward from the previous state, because it does not need
      // to be: the store refuses a finding-bound row that does not carry its own
      // binding, so every transition of a finding Krux already states the pin it
      // was accepted under. Reading it off the row keeps one source of truth.
      bindingKind: event.bindingKind,
      findingBinding: event.findingBinding,
      createdAt: existing?.createdAt ?? event.recordedAt,
      updatedAt: event.recordedAt,
      history: [event, ...(existing?.history ?? [])],
    });
  }
  return [...byId.values()].toSorted(
    (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
  );
}

/**
 * What may still be cited as the evidence behind one Krux, or `null` when
 * nothing may.
 *
 * The scan reference is offered only while the row it names is still retained.
 * Once retention has swept it (#3144) the reference proves nothing, so the
 * commit the scan measured — copied onto the transition when it was recorded —
 * stands in its place. With neither, nothing is cited: an outcome whose
 * evidence cannot be reached must not be rendered as evidenced.
 */
export function kruxCitableEvidence(krux: Readonly<Krux>): string | null {
  if (krux.evidence === 'resolved') return krux.scanId;
  // Only a swept reference falls back to the commit. `none` is a human decision
  // that never had a scan and `unrecorded` predates measurements, so neither has
  // evidence to offer even if a row somehow carried a measurement anyway.
  if (krux.evidence !== 'unresolvable') return null;
  const headSha = krux.scanEvidence?.headSha ?? '';
  return headSha === '' ? null : headSha;
}
